import type { NostrEvent } from '@nostrify/nostrify';
import type { MutableRefObject } from 'react';

import { APP_ALT } from '@/lib/stashstr';
import { minePow } from '@/lib/pow';

interface MineThenPublishArgs {
  userPubkey: string;
  publish: (t: {
    kind: number;
    content: string;
    tags: string[][];
    created_at: number;
  }) => Promise<NostrEvent>;
  kind: number;
  content: string;
  tags: string[][];
  difficulty: number;
  onProgress?: (tries: number) => void;
  shouldCancelRef: MutableRefObject<boolean>;
}

/**
 * Mines PoW for an event and publishes it such that the mined event id
 * matches the published event id exactly.
 *
 * Why this exists: `useNostrPublish` re-stamps `created_at` and injects a
 * `client` tag after signing. Both operations change the event id, which
 * invalidates any nonce mined beforehand (NIP-13 PoW is hash-based). This
 * helper captures `created_at` once, injects client/alt tags BEFORE mining,
 * then publishes with identical bytes.
 */
export async function mineThenPublish(args: MineThenPublishArgs): Promise<NostrEvent> {
  const { userPubkey, publish, kind, content, tags, difficulty, onProgress, shouldCancelRef } = args;

  // 1. Capture created_at ONCE — the mined nonce is only valid for this timestamp.
  const created_at = Math.floor(Date.now() / 1000);

  // 2. Inject client+alt BEFORE mining so the mined id matches the published id.
  //    (useNostrPublish skips its own client push when a client tag already exists.)
  const base = tags.filter(([t]) => t !== 'nonce' && t !== 'client' && t !== 'alt');
  const fullTags = [...base, ['client', 'stashstr.news'], ['alt', APP_ALT]];

  // 3. Mine with identical fields, checking the cancel ref (never stale state).
  const minedTags = await minePow(
    { kind, content, tags: fullTags, created_at, pubkey: userPubkey },
    difficulty,
    onProgress,
    () => shouldCancelRef.current,
  );

  // 4. Publish with the SAME created_at — never let the hook re-stamp it.
  return publish({ kind, content, tags: minedTags, created_at });
}
